// STARWEAVE — Spike B: cube-sphere planet with quadtree LOD.
//
// Proves the planetary render architecture: a 6-face cube projected to a
// sphere, each face an adaptive quadtree that subdivides by camera distance,
// displaced by deterministic value-noise terrain (seeded from the world-gen
// visual_seed so the surface is reproducible), plus an atmosphere shell and
// instanced surface rocks. Leaf meshes are cached and only rebuilt on
// split/merge. Coordinates are kept camera-friendly (planet radius in the
// hundreds of units) so float32 is precise from orbit to surface; true
// floating-origin rebasing is for inter-system scale (later milestone).

import * as THREE from 'three';

// --- deterministic value noise (no Math.random) ---------------------------
function hashLattice(ix: number, iy: number, iz: number, seed: number): number {
  let h = (Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(iz, 1274126177) ^ Math.imul(seed, 2147483647)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
}
function valueNoise(x: number, y: number, z: number, seed: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z);
  const fx = x - ix, fy = y - iy, fz = z - iz;
  const sm = (t: number) => t * t * (3 - 2 * t);
  const u = sm(fx), v = sm(fy), w = sm(fz);
  const L = THREE.MathUtils.lerp;
  const c = (dx: number, dy: number, dz: number) => hashLattice(ix + dx, iy + dy, iz + dz, seed);
  const x00 = L(c(0, 0, 0), c(1, 0, 0), u), x10 = L(c(0, 1, 0), c(1, 1, 0), u);
  const x01 = L(c(0, 0, 1), c(1, 0, 1), u), x11 = L(c(0, 1, 1), c(1, 1, 1), u);
  return L(L(x00, x10, v), L(x01, x11, v), w);
}
function fbm(x: number, y: number, z: number, seed: number, oct = 5): number {
  let a = 0.5, f = 1, sum = 0, norm = 0;
  for (let o = 0; o < oct; o++) {
    sum += a * valueNoise(x * f, y * f, z * f, seed + o * 131);
    norm += a; a *= 0.5; f *= 2.03;
  }
  return sum / norm;
}

interface Face {
  n: THREE.Vector3;
  a: THREE.Vector3;
  b: THREE.Vector3;
}
const FACES: Face[] = [
  { n: new THREE.Vector3(1, 0, 0), a: new THREE.Vector3(0, 1, 0), b: new THREE.Vector3(0, 0, 1) },
  { n: new THREE.Vector3(-1, 0, 0), a: new THREE.Vector3(0, 1, 0), b: new THREE.Vector3(0, 0, -1) },
  { n: new THREE.Vector3(0, 1, 0), a: new THREE.Vector3(0, 0, 1), b: new THREE.Vector3(1, 0, 0) },
  { n: new THREE.Vector3(0, -1, 0), a: new THREE.Vector3(0, 0, -1), b: new THREE.Vector3(1, 0, 0) },
  { n: new THREE.Vector3(0, 0, 1), a: new THREE.Vector3(0, 1, 0), b: new THREE.Vector3(-1, 0, 0) },
  { n: new THREE.Vector3(0, 0, -1), a: new THREE.Vector3(0, 1, 0), b: new THREE.Vector3(1, 0, 0) },
];

export interface PlanetParams {
  radius: number;        // scene units
  seed: number;          // terrain seed (from visual_seed)
  amplitude: number;     // max terrain height in scene units
  lowColor: THREE.Color; // valleys / sea level
  highColor: THREE.Color;// peaks
  detailTexture?: THREE.Texture; // triplanar rock detail (peaks/slopes) — GPT-Image art
  lowTexture?: THREE.Texture;    // valley ground cover (grass/sand) — GPT-Image art
}

const GRID = 16;          // verts per leaf edge
const MAX_DEPTH = 6;      // quadtree depth cap
const SPLIT_K = 2.2;      // distance threshold multiplier

export class Planet {
  readonly group = new THREE.Group();
  readonly params: PlanetParams;
  private material: THREE.MeshStandardMaterial;
  private roots: QuadNode[] = [];
  leafCount = 0;

  constructor(params: PlanetParams) {
    this.params = params;
    this.material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, metalness: 0.02 });
    if (params.detailTexture) this.applyDetail(params.detailTexture);
    for (const f of FACES) this.roots.push(new QuadNode(this, f, -1, 1, -1, 1, 0));
  }

  /** Triplanar-project a surface detail texture (no UVs needed), modulating
   *  the biome vertex colors with real rock/ground detail. */
  private applyDetail(rock: THREE.Texture): void {
    rock.wrapS = rock.wrapT = THREE.RepeatWrapping;
    const low = this.params.lowTexture;
    if (low) { low.wrapS = low.wrapT = THREE.RepeatWrapping; }
    const hasLow = !!low;
    this.material.onBeforeCompile = (shader) => {
      shader.uniforms.uRock = { value: rock };
      shader.uniforms.uTexScale = { value: 0.07 };
      shader.uniforms.uRadius = { value: this.params.radius };
      shader.uniforms.uAmp = { value: this.params.amplitude };
      if (hasLow) shader.uniforms.uLow = { value: low };
      shader.vertexShader = 'varying vec3 vWPos;\nvarying vec3 vWNorm;\n' +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n  vWPos = (modelMatrix*vec4(transformed,1.0)).xyz;\n  vWNorm = normalize(mat3(modelMatrix)*objectNormal);'
        );
      const lowDecl = hasLow ? 'uniform sampler2D uLow;\n' : '';
      const blend = hasLow
        ? `vec3 lowT = texture2D(uLow, vWPos.yz*uTexScale).rgb*bw.x + texture2D(uLow, vWPos.zx*uTexScale).rgb*bw.y + texture2D(uLow, vWPos.xy*uTexScale).rgb*bw.z;
           float hf = clamp((length(vWPos) - uRadius) / uAmp, 0.0, 1.0);
           vec3 tx = mix(lowT, rockT, smoothstep(0.30, 0.62, hf));`
        : `vec3 tx = rockT;`;
      shader.fragmentShader = `uniform sampler2D uRock;\nuniform float uTexScale;\nuniform float uRadius;\nuniform float uAmp;\n${lowDecl}varying vec3 vWPos;\nvarying vec3 vWNorm;\n` +
        shader.fragmentShader.replace(
          '#include <color_fragment>',
          `#include <color_fragment>
          {
            vec3 bw = abs(normalize(vWNorm)); bw = bw / (bw.x + bw.y + bw.z);
            vec3 rockT = texture2D(uRock, vWPos.yz*uTexScale).rgb*bw.x + texture2D(uRock, vWPos.zx*uTexScale).rgb*bw.y + texture2D(uRock, vWPos.xy*uTexScale).rgb*bw.z;
            ${blend}
            diffuseColor.rgb *= tx * 2.3;
          }`
        );
    };
  }

  /** Unit direction -> normalized terrain height in [0,1]. Rolling base
   *  combined with ridged noise for sharper mountain relief. */
  heightUnit(dir: THREE.Vector3): number {
    const base = fbm(dir.x * 1.6 + 11, dir.y * 1.6 + 5, dir.z * 1.6 + 23, this.params.seed, 7);
    const r = fbm(dir.x * 3.1 + 50, dir.y * 3.1 + 9, dir.z * 3.1 + 77, this.params.seed + 9173, 5);
    const ridge = 1 - Math.abs(r * 2 - 1);
    return Math.min(1, base * 0.62 + ridge * ridge * 0.5);
  }

  /** Surface point (with terrain) for a unit direction. */
  surfacePoint(dir: THREE.Vector3): THREE.Vector3 {
    const h = this.heightUnit(dir);
    return dir.clone().multiplyScalar(this.params.radius + h * this.params.amplitude);
  }

  meshMaterial(): THREE.MeshStandardMaterial { return this.material; }

  /** Re-evaluate LOD against the camera position (planet-local space). */
  update(cameraPos: THREE.Vector3): void {
    this.leafCount = 0;
    for (const r of this.roots) r.update(cameraPos);
    // count leaves with live meshes
    for (const r of this.roots) this.leafCount += r.countLeaves();
  }
}

class QuadNode {
  private children: QuadNode[] | null = null;
  private mesh: THREE.Mesh | null = null;
  private readonly center: THREE.Vector3;
  private readonly nodeRadius: number;

  constructor(
    private planet: Planet,
    private face: Face,
    private u0: number, private u1: number,
    private v0: number, private v1: number,
    private depth: number,
  ) {
    const um = (u0 + u1) / 2, vm = (v0 + v1) / 2;
    this.center = planet.surfacePoint(cubeDir(face, um, vm));
    // approximate chord radius of this patch
    const corner = planet.surfacePoint(cubeDir(face, u0, v0));
    this.nodeRadius = corner.distanceTo(this.center);
  }

  countLeaves(): number {
    if (this.children) return this.children.reduce((s, c) => s + c.countLeaves(), 0);
    return this.mesh ? 1 : 0;
  }

  update(cameraPos: THREE.Vector3): void {
    const dist = cameraPos.distanceTo(this.center);
    const wantSplit = this.depth < MAX_DEPTH && dist < this.nodeRadius * SPLIT_K * (1 << 1);
    if (wantSplit) {
      if (this.mesh) { this.planet.group.remove(this.mesh); disposeMesh(this.mesh); this.mesh = null; }
      if (!this.children) {
        const um = (this.u0 + this.u1) / 2, vm = (this.v0 + this.v1) / 2;
        this.children = [
          new QuadNode(this.planet, this.face, this.u0, um, this.v0, vm, this.depth + 1),
          new QuadNode(this.planet, this.face, um, this.u1, this.v0, vm, this.depth + 1),
          new QuadNode(this.planet, this.face, this.u0, um, vm, this.v1, this.depth + 1),
          new QuadNode(this.planet, this.face, um, this.u1, vm, this.v1, this.depth + 1),
        ];
      }
      for (const c of this.children) c.update(cameraPos);
    } else {
      if (this.children) { for (const c of this.children) c.dispose(); this.children = null; }
      if (!this.mesh) { this.mesh = this.build(); this.planet.group.add(this.mesh); }
    }
  }

  private build(): THREE.Mesh {
    const { radius, amplitude, lowColor, highColor } = this.planet.params;
    const seed = this.planet.params.seed;
    const rock = lowColor.clone().multiplyScalar(0.5);
    const n = GRID;
    const positions = new Float32Array(n * n * 3);
    const colors = new Float32Array(n * n * 3);
    const tmp = new THREE.Vector3();
    const col = new THREE.Color();
    let p = 0;
    for (let j = 0; j < n; j++) {
      for (let i = 0; i < n; i++) {
        const u = THREE.MathUtils.lerp(this.u0, this.u1, i / (n - 1));
        const v = THREE.MathUtils.lerp(this.v0, this.v1, j / (n - 1));
        const dir = cubeDir(this.face, u, v, tmp);
        const h = this.planet.heightUnit(dir);
        const r = radius + h * amplitude;
        positions[p * 3] = dir.x * r;
        positions[p * 3 + 1] = dir.y * r;
        positions[p * 3 + 2] = dir.z * r;
        // Biome bands: dust valleys -> rock -> light highlands, + detail tint.
        if (h < 0.42) col.copy(lowColor).lerp(rock, h / 0.42);
        else col.copy(rock).lerp(highColor, (h - 0.42) / 0.58);
        const tint = 0.8 + valueNoise(dir.x * 9 + 3, dir.y * 9 + 1, dir.z * 9 + 7, seed + 31) * 0.4;
        col.multiplyScalar(tint);
        colors[p * 3] = col.r; colors[p * 3 + 1] = col.g; colors[p * 3 + 2] = col.b;
        p++;
      }
    }
    const indices: number[] = [];
    for (let j = 0; j < n - 1; j++) {
      for (let i = 0; i < n - 1; i++) {
        const a = j * n + i, b = a + 1, c = a + n, d = c + 1;
        indices.push(a, c, b, b, c, d);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    return new THREE.Mesh(geo, this.planet.meshMaterial());
  }

  dispose(): void {
    if (this.children) { for (const c of this.children) c.dispose(); this.children = null; }
    if (this.mesh) { this.planet.group.remove(this.mesh); disposeMesh(this.mesh); this.mesh = null; }
  }
}

function cubeDir(face: Face, u: number, v: number, out = new THREE.Vector3()): THREE.Vector3 {
  out.copy(face.n).addScaledVector(face.a, u).addScaledVector(face.b, v);
  return out.normalize();
}

function disposeMesh(m: THREE.Mesh): void {
  m.geometry.dispose();
}

/** Atmospheric scattering shell: Rayleigh-style limb + Mie sun-side glow
 *  (additive, back faces). Bright enough to catch the bloom pass. */
export function makeAtmosphere(
  radius: number,
  color: THREE.Color,
  intensity = 0.6,
  sunDir: THREE.Vector3 = new THREE.Vector3(1, 0, 0),
): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    uniforms: {
      uColor: { value: color },
      uIntensity: { value: intensity },
      uSun: { value: sunDir.clone().normalize() },
    },
    vertexShader: `
      varying vec3 vN; varying vec3 vP; varying vec3 vWdir;
      void main(){
        vN = normalize(normalMatrix * normal);
        vWdir = normalize((modelMatrix * vec4(position,1.0)).xyz);
        vec4 mv = modelViewMatrix * vec4(position,1.0);
        vP = mv.xyz;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: `
      uniform vec3 uColor; uniform float uIntensity; uniform vec3 uSun;
      varying vec3 vN; varying vec3 vP; varying vec3 vWdir;
      void main(){
        vec3 vd = normalize(-vP);
        float rim = pow(1.0 - max(dot(vN, vd), 0.0), 3.2);      // limb glow
        float sun = max(dot(vWdir, normalize(uSun)), 0.0);
        float glow = pow(sun, 8.0) * 0.9;                       // sun-side Mie glow
        float e = (rim + glow) * uIntensity;
        gl_FragColor = vec4(uColor * e * 1.8, clamp(e, 0.0, 1.0));
      }
    `,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(radius, 64, 64), mat);
}

// Shared GLSL value-noise / fbm for the procedural sky, clouds, and ocean.
const GLSL_NOISE = `
float hash13(vec3 p){ p=fract(p*0.3183099+0.1); p*=17.0; return fract(p.x*p.y*p.z*(p.x+p.y+p.z)); }
float vnoise(vec3 x){ vec3 i=floor(x); vec3 f=fract(x); f=f*f*(3.0-2.0*f);
  return mix(mix(mix(hash13(i+vec3(0,0,0)),hash13(i+vec3(1,0,0)),f.x),
                 mix(hash13(i+vec3(0,1,0)),hash13(i+vec3(1,1,0)),f.x),f.y),
             mix(mix(hash13(i+vec3(0,0,1)),hash13(i+vec3(1,0,1)),f.x),
                 mix(hash13(i+vec3(0,1,1)),hash13(i+vec3(1,1,1)),f.x),f.y),f.z); }
float fbm3(vec3 p){ float a=0.5; float s=0.0; for(int i=0;i<5;i++){ s+=a*vnoise(p); p*=2.03; a*=0.5; } return s; }
`;

/** Procedural deep-space skybox: nebula clouds + sparse stars. */
export function makeSkybox(nebA: THREE.Color, nebB: THREE.Color): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    uniforms: { uNebA: { value: nebA }, uNebB: { value: nebB } },
    vertexShader: `varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
    fragmentShader: GLSL_NOISE + `
      uniform vec3 uNebA; uniform vec3 uNebB; varying vec3 vDir;
      void main(){
        vec3 d = normalize(vDir);
        float n = fbm3(d * 3.0 + 10.0);
        float n2 = fbm3(d * 7.0 + 40.0);
        float neb = smoothstep(0.46, 0.96, n * 0.65 + n2 * 0.45);
        vec3 col = vec3(0.006, 0.008, 0.02);
        col += mix(uNebA, uNebB, n2) * neb * 0.6;
        float s = pow(hash13(floor(d * 900.0) + 0.5), 72.0);
        col += vec3(s) * 2.2;
        gl_FragColor = vec4(col, 1.0);
      }`,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(18000, 32, 32), mat);
}

/** Animated cloud shell (lit by the sun). Update material.uniforms.uTime. */
export function makeClouds(radius: number, sunDir: THREE.Vector3): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: { uSun: { value: sunDir.clone().normalize() }, uTime: { value: 0 } },
    vertexShader: `varying vec3 vN; varying vec3 vW; void main(){ vN = normalize(normalMatrix*normal); vW = normalize((modelMatrix*vec4(position,1.0)).xyz); gl_Position = projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader: GLSL_NOISE + `
      uniform vec3 uSun; uniform float uTime; varying vec3 vN; varying vec3 vW;
      void main(){
        vec3 d = vW;
        float c = fbm3(d * 4.0 + vec3(uTime * 0.012, 0.0, uTime * 0.008));
        float cov = smoothstep(0.5, 0.78, c);
        if (cov < 0.02) discard;
        float lt = clamp(dot(normalize(vN), normalize(uSun)) * 0.5 + 0.62, 0.0, 1.0);
        gl_FragColor = vec4(vec3(1.0, 0.98, 0.95) * lt, cov * 0.82);
      }`,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(radius, 64, 64), mat);
}

/** Ocean shell with fresnel + animated sun specular. Update uTime. */
export function makeOcean(radius: number, color: THREE.Color, sunDir: THREE.Vector3): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    uniforms: { uColor: { value: color }, uSun: { value: sunDir.clone().normalize() }, uTime: { value: 0 } },
    vertexShader: `varying vec3 vN; varying vec3 vWP; varying vec3 vView;
      void main(){ vN = normalize(normalMatrix*normal); vec4 wp = modelMatrix*vec4(position,1.0); vWP = wp.xyz; vec4 mv = modelViewMatrix*vec4(position,1.0); vView = normalize(-mv.xyz); gl_Position = projectionMatrix*mv; }`,
    fragmentShader: GLSL_NOISE + `
      uniform vec3 uColor; uniform vec3 uSun; uniform float uTime; varying vec3 vN; varying vec3 vWP; varying vec3 vView;
      void main(){
        float w = fbm3(vWP * 0.05 + vec3(uTime * 0.08, 0.0, 0.0));
        vec3 wn = normalize(vN + (w - 0.5) * 0.2);
        float fres = pow(1.0 - max(dot(wn, vView), 0.0), 3.0);
        vec3 h = normalize(normalize(uSun) + vView);
        float spec = pow(max(dot(wn, h), 0.0), 90.0);
        vec3 col = mix(uColor * 0.55, uColor * 1.25, fres) + vec3(1.0, 0.97, 0.9) * spec * 1.8;
        gl_FragColor = vec4(col, 0.92);
      }`,
  });
  return new THREE.Mesh(new THREE.SphereGeometry(radius, 96, 96), mat);
}

/** Scatter instanced rocks near a surface anchor direction (instancing proof). */
export function makeRocks(planet: Planet, anchorDir: THREE.Vector3, count: number, seed: number): THREE.InstancedMesh {
  const geo = new THREE.IcosahedronGeometry(planet.params.amplitude * 0.05, 0);
  const mat = new THREE.MeshStandardMaterial({ color: 0x6b6256, roughness: 1, flatShading: true });
  const inst = new THREE.InstancedMesh(geo, mat, count);
  const m = new THREE.Matrix4();
  const tangent = new THREE.Vector3(0, 1, 0).cross(anchorDir).normalize();
  const bitan = anchorDir.clone().cross(tangent).normalize();
  for (let k = 0; k < count; k++) {
    const ang = valueNoise(k * 1.7, 3, 9, seed) * Math.PI * 2;
    const rad = valueNoise(k * 0.7, 11, 2, seed) * planet.params.radius * 0.04;
    const dir = anchorDir.clone()
      .addScaledVector(tangent, Math.cos(ang) * rad / planet.params.radius)
      .addScaledVector(bitan, Math.sin(ang) * rad / planet.params.radius)
      .normalize();
    const pos = planet.surfacePoint(dir);
    const s = 0.5 + valueNoise(k, 7, 1, seed) * 1.5;
    m.makeScale(s, s, s);
    m.setPosition(pos.x, pos.y, pos.z);
    inst.setMatrixAt(k, m);
  }
  inst.instanceMatrix.needsUpdate = true;
  return inst;
}
